// cleanup_convert_to_glu_component_GENERICS

import React, { useState, useMemo, useEffect, FC } from 'react';

import gluLocale from '@glu/locale';
import {
  Dropdown as ValidatedDropdown,
  defaultStrings_en_US as dropdownStrings
} from '@glu/dropdown-react';

import Typography, { variants } from 'components/typography/Typography';
import useLocale from 'components/useLocale';
import { numberToCurrency } from 'utils/currency';
import { COLUMN_TYPES } from 'constants.js';

import useAccountBalance from './useAccountBalance';
import useStyles from './BalanceDropdown.styles';
import type { IOptions, IServiceOptions } from './BalanceDropdown.types';

gluLocale.set(dropdownStrings);

interface Props {
  formChange?: (name: string, value: unknown, validate?: boolean) => void;
  formValidate?: (field: string) => void;
  formValue?: Array<IOptions>;
  formData?: Record<string, Array<IOptions>>;
  labelText: string;
  htmlId: string;
  name: string;
  onChange?: (name: string, value: unknown) => void;
  options: Array<IOptions>;
  service: IServiceOptions;
  getBalanceLabel?: () => string;
  preSelectedId?: string | number;
  filterEnabled?: boolean;
  dataQa?: string;
  validators?: Array<unknown>;
}

// NOTE: balanceKey and getBalance are required because all the apis returning data
// for these dropdowns have different structure
// balanceKey: maps to the key showing account balance inside the options passed to dropdown
// getBalance: fetches balance from the response after call to url is successful
const BalanceDropdown: FC<Props> = ({
  formChange,
  formData,
  formValidate,
  formValue,
  getBalanceLabel,
  labelText,
  name,
  onChange,
  options,
  preSelectedId,
  service: { api, balanceKey, getBalance, payload },
  ...props
}) => {
  const [updatedOptionId, setUpdatedOptionId] = useState<string | number | null>(null);
  const { balance, labelContainer } = useStyles();
  const locale = useLocale();
  // NOTE: even if a single account has balance 'null', it means api will have to be called individually
  // for selected account ids to fetch the balance. If account has no balance api will return 0 and not null
  // type BalanceKeyType typeof OptionsType;
  const hasNoBalanceInfo = options.some(
    (option) => !(balanceKey in option) || option[balanceKey as keyof IOptions] === null
  );
  const [selectedId, setSelectedId] = useState(preSelectedId || null);
  const [isSelectedTierLoan, setIsSelectedTierLoan] = useState(false);
  const [availableBalance, fetchedAccount, balanceLabel] = useAccountBalance({
    api,
    accountId: selectedId,
    payload,
    getBalance,
    getBalanceLabel,
    enabled: hasNoBalanceInfo && Boolean(selectedId)
  });

  const dropdownOptions = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        ...(!option.selected && {
          name:
            hasNoBalanceInfo || !option.name
              ? `${option.nickname || option.standardName || option.displayName} - ${
                  option.accountNumber || option.number
                }`
              : option.name
        }),
        ...(hasNoBalanceInfo && availableBalance != null && option.id === selectedId
          ? { [balanceKey]: availableBalance }
          : {})
      })),
    [hasNoBalanceInfo, options, availableBalance, balanceKey, selectedId]
  );

  useEffect(() => {
    if (
      hasNoBalanceInfo &&
      fetchedAccount &&
      availableBalance != null &&
      updatedOptionId !== fetchedAccount
    ) {
      setUpdatedOptionId(fetchedAccount);
      if (formChange)
        formChange(
          name,
          dropdownOptions.filter(({ id }) => id === fetchedAccount)
        );
      if (formValidate) formValidate(COLUMN_TYPES.AMOUNT);
    }
  }, [
    dropdownOptions,
    availableBalance,
    fetchedAccount,
    formChange,
    name,
    updatedOptionId,
    formValidate,
    hasNoBalanceInfo
  ]);

  // Clear the balance displayed on top of the dropdown when the SMB Funding Account is reset
  useEffect(() => {
    if (formData && selectedId && formData?.[name]?.length === 0) {
      setSelectedId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.[name]]);

  // eslint-disable-next-line
  const hasAvailableBalance = availableBalance != undefined;
  return (
    <ValidatedDropdown
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      name={name}
      labelText={
        <span className={labelContainer}>
          <span>{labelText}</span>

          {/** NOTE: this functionality is going to be added to glu date picker as part of
           * https://jira.bottomline.tech/browse/GLU-5877
           * the following can be replaced once GLU-5877 is closed
           */}
          {(!formValue || formValue?.[0]?.id) &&
            !isSelectedTierLoan &&
            hasNoBalanceInfo &&
            hasAvailableBalance && (
              <span className={balance} data-qa="available-balance">
                <Typography variant={variants.smallLabel} tag="span">
                  {`${balanceLabel || locale['common.available']}: ${numberToCurrency(
                    availableBalance
                  )}`}
                </Typography>
              </span>
            )}
        </span>
      }
      matchBy={['name']}
      options={dropdownOptions}
      onChange={(_name, value) => {
        if (hasNoBalanceInfo) {
          setSelectedId(value?.[0]?.id);
        }
        if (onChange) onChange(_name, value);
        setIsSelectedTierLoan(Boolean(value?.[0]?.tieredLoan));
      }}
    />
  );
};

export default BalanceDropdown;
